import { NavLink } from 'react-router-dom';
import { Bike, History, UserRound } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * The three things a rider does: work the run sheet, look back at what they
 * finished, and manage their own shift.
 *
 * Three tabs rather than the customer app's four, and no search anywhere —
 * this app has no catalogue and nothing to browse. What a rider does is
 * whatever is at the top of one list.
 */
const TABS = [
  { to: '/jobs', label: 'Jobs', icon: Bike },
  { to: '/history', label: 'History', icon: History },
  { to: '/account', label: 'You', icon: UserRound },
];

export function TabBar({ activeCount = 0 }: { activeCount?: number }) {
  return (
    <nav
      aria-label="Sections"
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 border-t border-line',
        'bg-page/90 backdrop-blur-xl supports-[not(backdrop-filter:blur(0))]:bg-page',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <ul className="mx-auto grid max-w-app grid-cols-3">
        {TABS.map((tab) => {
          // The count is jobs OUTSTANDING, not jobs done today: a rider
          // glancing at the tab bar wants to know whether they are finished.
          const badge = tab.to === '/jobs' && activeCount > 0;

          return (
            <li key={tab.to}>
              <NavLink
                to={tab.to}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-tabbar flex-col items-center justify-center gap-1',
                    'text-[0.6875rem] font-semibold',
                    'transition-colors duration-[var(--motion-duration-fast)] ease-standard',
                    isActive ? 'text-brand' : 'text-ink-subtle',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative">
                      <tab.icon
                        className="size-[1.375rem]"
                        strokeWidth={isActive ? 2.4 : 1.9}
                        aria-hidden="true"
                      />
                      {badge ? (
                        <span
                          data-numeric
                          aria-hidden="true"
                          className="absolute -right-2.5 -top-1.5 grid min-w-[1.125rem] place-items-center rounded-full bg-primary px-1 text-[0.625rem] font-bold leading-[1.125rem] text-on-primary"
                        >
                          {activeCount > 9 ? '9+' : activeCount}
                        </span>
                      ) : null}
                    </span>
                    {tab.label}
                    {/*
                     * A second marker on top of the colour: the active tab
                     * also carries a rule, so "which tab am I on" does not
                     * depend on telling green from grey in sunlight.
                     */}
                    {isActive ? (
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-8 top-0 h-0.5 rounded-b-full bg-primary"
                      />
                    ) : null}
                    {badge ? (
                      <span className="sr-only-text">
                        {activeCount} job{activeCount === 1 ? '' : 's'} outstanding
                      </span>
                    ) : null}
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
